import React, { useEffect, useState } from 'react'
import { authService, type MfaEnrollmentDetails } from '../../services/auth'
import { useAuth } from '../../context/AuthContext'
import { MfaEnrollment } from './MfaEnrollment'

type Status = { enabled: boolean; enrollmentAvailable: boolean; tenantId: string | null; entitled: boolean }
type Policy = { policy: string; entitled: boolean; canManagePolicy: boolean }

/** Account security uses the same entitlement and policy APIs as authentication. */
export const MfaSettings: React.FC = () => {
  const auth = useAuth()
  const [status, setStatus] = useState<Status | null>(null)
  const [policy, setPolicy] = useState<Policy | null>(null)
  const [enrollment, setEnrollment] = useState<MfaEnrollmentDetails | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>(() => auth.getPendingMfaRecoveryCodes())
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = async () => {
    const response = await authService.getMfaStatus()
    setStatus(response.data)
    if (response.data.tenantId) setPolicy((await authService.getTenantMfaPolicy()).data)
  }
  useEffect(() => { refresh().catch(() => setMessage('MFA settings could not be loaded.')) }, [])

  const perform = async (operation: () => Promise<void>) => {
    setBusy(true)
    setMessage('')
    try { await operation() }
    catch (error: any) { setMessage(error.response?.data?.message || error.response?.data?.error?.message || 'MFA settings could not be updated.') }
    finally { setBusy(false) }
  }

  return <section className="card stack" aria-label="Multi-factor authentication">
    <h3>Multi-factor authentication</h3>
    {message ? <p role="status">{message}</p> : null}
    {recoveryCodes.length ? <div className="stack">
      <p>Save your recovery codes before continuing. They will not be shown again.</p>
      <pre>{recoveryCodes.join('\n')}</pre>
      <button className="btn btn-primary" disabled={busy} onClick={() => perform(async () => {
        await auth.acknowledgeMfaRecoveryCodes(); setRecoveryCodes([]); await refresh()
      })}>I saved my recovery codes</button>
    </div> : enrollment ? <MfaEnrollment enrollment={enrollment} loading={busy} error={message}
      verificationCode={code} onVerificationCodeChange={setCode}
      onStart={() => {}} onCancel={() => { setEnrollment(null); setCode('') }}
      onExpire={() => { setEnrollment(null); setCode(''); setMessage('Enrollment expired. Start again.') }}
      onConfirm={event => { event.preventDefault(); void perform(async () => {
        const result = await auth.confirmMfaEnrollment(code)
        setRecoveryCodes(result.recoveryCodes || [])
        setEnrollment(null); setCode('')
      }) }} /> : status ? <>
      {!status.entitled && !status.enrollmentAvailable ? <p>MFA is an available add-on. Ask your platform billing administrator to enable access for this tenant.</p> : null}
      {status.enabled ? <p>Your authenticator remains enrolled. Each tenant applies its own MFA policy.</p> : status.enrollmentAvailable ?
        <button className="btn btn-secondary" disabled={busy} onClick={() => perform(async () => {
          const result = await auth.startMfaEnrollment(); setEnrollment(result.enrollment)
        })}>Set up an authenticator</button> : null}
      {policy?.canManagePolicy && policy.entitled ? <label>Tenant MFA policy
        <select className="input" value={policy.policy} disabled={busy} onChange={event => {
          const value = event.target.value
          void perform(async () => {
            await authService.updateTenantMfaPolicy(value)
            setPolicy({ ...policy, policy: value })
            // The newly saved mandate may immediately restrict this session.
            await refresh().catch(() => {})
            setMessage('Policy saved. Sign in again if this policy requires MFA for your account.')
          })
        }}>
          <option value="optional">Optional enrollment</option>
          <option value="required_admins">Required for administrators</option>
          <option value="required_privileged">Required for privileged users</option>
          <option value="required_all">Required for all tenant users</option>
          <option value="disabled">Enrollment disabled</option>
        </select>
      </label> : null}
    </> : null}
  </section>
}
